import { useState, useEffect, useContext, useSelector } from "@/common/hooks";
import {
  Spinner,
  InputField,
  CommonLink,
  Button,
  ErrorMessage,
  InfoPopup,
  MaterialIcon,
} from "@/common/components";
import { useLoginQuery, useForgotPasswordQuery } from "@/service/APIService";
import {
  setToken,
  setUsernameInStore,
  setPasswordInStore,
} from "@/store/authSlice";
import { PublicContext } from "@/routes/PublicRoute";
import { validate } from "@/utils/Validations";
import { encryptData } from "@/utils/Utilities";

const Login = () => {
  const {
    labels,
    Routes,
    skipToken,
    navigate,
    dispatch,
    uniqueId,
    debounceState,
    debounceErrors,
    cloneDeep,
  } = useContext(PublicContext);
  const tokenFromStore = useSelector((state) => state?.authReducer?.token);
  const [operatorId, setOperatorId] = useState();
  const [password, setPassword] = useState();
  const [fetchTokenApi, setFetchTokenApi] = useState(skipToken);
  const [forgotPasswordApi, setForgotPasswordApi] = useState(skipToken);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [latestError, setLatestError] = useState(null);

  const {
    data: token,
    isFetching,
    error: tokenError,
  } = useLoginQuery(fetchTokenApi);
  const {
    data: forgotPasswordData,
    isFetching: isFetching1,
    error: forgotPasswordError,
  } = useForgotPasswordQuery(forgotPasswordApi);

  const doLogin = async () => {
    const payload = {
      username: await encryptData(operatorId.toLowerCase()),
      password: await encryptData(password),
      id: uniqueId(),
    };
    setFetchTokenApi(payload);
  };

  const doForgotPassword = () => {
    setForgotPasswordApi({
      operatorId: operatorId,
      id: uniqueId(),
    });
  };

  const doExpiredPassword = async () => {
    let routeData = {
      username: await encryptData(operatorId.toLowerCase()),
      password: await encryptData(password),
    };
    navigate(Routes.forgotPassword.path, {
      state: routeData,
    });
  };

  const handleForgotPassword = async () => {
    let routeData = cloneDeep(forgotPasswordData);
    routeData.password = showPopup ? await encryptData(password) : "";
    navigate(Routes.forgotPassword.path, {
      state: routeData,
    });
  };

  const close = () => {
    setShowPopup(false);
    navigate(0);
  };

  useEffect(() => {
    if (token && token?.access_token) {
      dispatch(setToken(token));
      dispatch(setUsernameInStore(fetchTokenApi?.username));
      dispatch(setPasswordInStore(fetchTokenApi?.password));
      navigate(Routes.dashboard.path);
    }
  }, [token]);

  useEffect(() => {
    if (forgotPasswordData && forgotPasswordData?.customUserId) {
      handleForgotPassword();
    }
  }, [forgotPasswordData]);

  useEffect(() => {
    if (tokenError?.data?.errors) {
      setLatestError(tokenError);
      if (tokenError?.data?.errors[0]?.name === "PASSWORD") {
        setShowPopup(true);
      }
    }
  }, [tokenError]);

  useEffect(() => {
    if (forgotPasswordError) setLatestError(forgotPasswordError);
  }, [forgotPasswordError]);

  useEffect(() => {
    if (tokenFromStore && tokenFromStore?.access_token) {
      navigate(Routes.dashboard.path);
    }
  }, []);

  return (
    <main className="login" id="login">
      {(isFetching || isFetching1) && <Spinner />}
      <section className="loginImage d-none d-md-block" id="loginImage" />
      <section className="row py-5">
        <section className="left col-md-5">
          <div className="description">{labels?.login?.titleDescription}</div>
          <MaterialIcon iconName="pending" wrapperClassName="invisible" />
        </section>
        <section className="col-md-1 border-right-1 "></section>
        <section className="right col-md-6">
          {!showPopup && (
            <ErrorMessage
              error={latestError}
              wrapperClassName="col-md-8 col-12 mb-2"
            />
          )}
          <InputField
            id="username"
            label={labels?.login?.operatorId}
            wrapperClassName="col-md-8 col-12"
            value={operatorId}
            change={async (e) => {
              debounceState(e, setOperatorId);
              debounceErrors(
                {
                  ...errors,
                  operatorId: await validate(e, "operatorId"),
                },
                setErrors
              );
            }}
            error={errors.operatorId}
            touch={() => setTouched({ ...touched, operatorId: true })}
            touched={touched.operatorId}
          />

          <InputField
            id="password"
            type="password"
            maxLength="20"
            label={labels?.login?.password}
            wrapperClassName="col-md-8 col-12"
            value={password}
            change={async (e) => {
              debounceState(e, setPassword);
              debounceErrors(
                {
                  ...errors,
                  password: await validate(e, "password"),
                },
                setErrors
              );
            }}
            error={errors.password}
            touch={() => setTouched({ ...touched, password: true })}
            touched={touched.password}
          />
          <Button
            id="login"
            text={labels?.login?.logIn}
            wrapperClassName="col-md-4 col-12 my-3"
            click={doLogin}
            disabled={
              !operatorId ||
              !password ||
              Object.values(errors).some((val) => val)
            }
          />

          <CommonLink
            id="forgotOperatorId"
            text={labels?.login?.forgotOperatorId}
            path={Routes.forgotOperatorId.path}
            wrapperClassName="mb-2"
          />
          <CommonLink
            id="forgotPassword"
            text={labels?.login?.forgotPassword}
            click={doForgotPassword}
            disabled={!operatorId}
            wrapperClassName="mb-2"
          />
          <CommonLink
            id="requestNewOperatorAccess"
            text={labels?.login?.requestNewOperatorAccess}
            path={Routes.requestNewOperator.path}
          />
        </section>
      </section>
      <InfoPopup
        showModal={showPopup}
        primaryButtonText={labels?.login?.changePassword}
        primaryButtonClick={doExpiredPassword}
        primaryButtonClassName="col-md-12"
        secondaryButtonText={labels?.login?.cancel}
        secondaryButtonClick={close}
        secondaryButtonClassName="col-md-12"
        body={labels?.login?.passwordExpired}
      />
    </main>
  );
};

export default Login;
